// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
export class SI extends IndicatorScriptBase {
    constructor() {
        super();
        // #region Initialization
        this.ProjectName = 'Swing Index';
        this.Comments = 'Is used to confirm trend line breakouts on price charts';
        // #endregion
        this.SetIndicatorLine('SI', Color.Red, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.T = 300;
        super.InputParameter(new InputParameterInteger('T', 'T', 0, 1, 99999, 0, 1));
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.UpdateIndexDrawBegin();
    }
    GetIndicatorShortName() {
        return 'SI(' + this.T + ')';
    }
    getCustomName() {
        return 'SI (' + this.ProjectName + ')';
    }
    OnQuote() {
        super.OnQuote();
        if (this.CurrentData.Count < 2) {
            return;
        }
        const prevOpen = this.CurrentData.getPriceByType(PriceType.Open, 1);
        const prevClose = this.CurrentData.getPriceByType(PriceType.Close, 1);
        const prevHigh = this.CurrentData.getPriceByType(PriceType.High, 1);
        const prevLow = this.CurrentData.getPriceByType(PriceType.Low, 1);
        const open = this.CurrentData.getPriceByType(PriceType.Open, 0);
        const close = this.CurrentData.getPriceByType(PriceType.Close, 0);
        // Calculate K: max(|H_y - C|, |L_y - C|)
        const K = Math.max(Math.abs(prevHigh - close), Math.abs(prevLow - close));
        // Calculate the three candidate differences for determining which case to use
        const d1 = Math.abs(prevHigh - close);
        const d2 = Math.abs(prevLow - close);
        const d3 = Math.abs(prevHigh - prevLow);
        // Determine R based on which difference is largest
        let R = 0;
        const maxDiff = Math.max(d1, d2, d3);
        // Handle the rare case where all three are equal or very close
        if (maxDiff < 0.000001) {
            this.SetValue(0, 0, 0);
            return;
        }
        // Determine which case applies based on maxDiff
        if (Math.abs(maxDiff - d1) < 0.000001) {
            // Case 1: maxDiff is d1 = |H_y - C|
            R = d1 - 0.5 * d2 + 0.25 * Math.abs(close - open);
        }
        else if (Math.abs(maxDiff - d2) < 0.000001) {
            // Case 2: maxDiff is d2 = |L_y - C|
            R = d2 - 0.5 * d1 + 0.25 * Math.abs(close - open);
        }
        else {
            // Case 3: maxDiff is d3 = |H_y - L_y|
            R = d3 + 0.25 * Math.abs(close - open);
        }
        // Calculate the numerator: C_y - C + 0.5*(C_y - O_y) + 0.25*(C - O)
        const numerator = prevClose - close + 0.5 * (prevClose - prevOpen) + 0.25 * (close - open);
        // Calculate SI: 50 * numerator * K / (R * T)
        let si = 0;
        if (Math.abs(R) >= 0.000001) {
            si = (50 * numerator * K) / (R * this.T);
        }
        this.SetValue(0, 0, si);
    }
}
